import { JsonArray, JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { Scenario } from '../../../../Assembler/dist/config/iConfig.js';

/**
 * Represents a scenario decorated with UI-friendly display metadata.
 */
export class EnrichedScenario {
    appSite: string;
    appView: string;
    displayName: string;
    description: string;
    sourceFolder: string;

    constructor(appSite: string = '', appView: string = '', displayName: string = '', description: string = '', sourceFolder: string = '') {
        this.appSite = appSite;
        this.appView = appView;
        this.displayName = displayName;
        this.description = description;
        this.sourceFolder = sourceFolder;
    }
}

/**
 * The scenario picker's SHARED, PURE enrich/parse/filter/build logic — the single source of truth for the
 * scenario list, consumed identically by every tier.
 *
 * Design:
 * This class is deliberately DEPENDENCY-FREE: no file IO, no ConfigFile, no registry, no handler. It exists so
 * that no handler has to depend on another handler to reuse this logic — ScenariosData (server data provider),
 * ScenarioClientData (server raw preparer) and ScenarioClient (client processor) each depend ONLY on this
 * class, never on each other. Because it is pure it bundles into the browser untouched, which is what lets the
 * client processor run the byte-identical filter in the browser, in --apiclient and in --ociclient (the
 * single-source guarantee, §3.7). The Scenario import is type-only, so it erases at build and drags no config
 * dependency into the bundle.
 *
 * The readme text arrives already resolved on the Scenario model (stamped by
 * ConfigFile.buildScenariosFromMetadata from the index): the readme is read once at index build, never by a
 * handler and never per merge.
 */
export class ScenariosFilter {
    /**
     * Gets enriched scenarios with displayName and description
     * Can be used by both IData implementation and API endpoints
     */
    static getEnrichedScenarios(scenarios: Scenario[], paramJson: JsonObject | null = null): EnrichedScenario[] {
        const result: EnrichedScenario[] = [];

        if (scenarios === null || scenarios === undefined || scenarios.length === 0) {
            return result;
        }

        for (let i = 0; i < scenarios.length; i++) {
            const enriched = ScenariosFilter._getEnrichedScenario(scenarios[i], paramJson);
            result.push(enriched);
        }

        return result;
    }

    /**
     * Converts a raw scenarios JsonArray (as produced by ScenariosData.getRawData /
     * ScenarioClientData.getClientData) into a list of EnrichedScenario. Shared by the server processData and
     * the client processor so both parse the raw island/slice identically.
     */
    static parseEnrichedScenarios(rawScenariosArr: JsonArray | null): EnrichedScenario[] {
        const scenarios: EnrichedScenario[] = [];
        const len = rawScenariosArr !== null ? rawScenariosArr.length : 0;
        for (let i = 0; i < len; i++) {
            const item = rawScenariosArr !== null ? rawScenariosArr.get(i) : null;
            if (item !== null && item !== undefined) {
                scenarios.push(new EnrichedScenario(
                    ScenariosFilter._getScenarioValueStatic(item, 'appSite'),
                    ScenariosFilter._getScenarioValueStatic(item, 'appView'),
                    ScenariosFilter._getScenarioValueStatic(item, 'displayName'),
                    ScenariosFilter._getScenarioValueStatic(item, 'description'),
                    ScenariosFilter._getScenarioValueStatic(item, 'sourceFolder')
                ));
            }
        }
        return scenarios;
    }

    /**
     * Applies the scenario picker's pure filter: keep only the selected folder (when specified), then drop
     * SkipViewAppSitesFolder folders and SkipViewAppSites appsites. Case-insensitive (matching C#). Shared by
     * the server processData and the browser/console client processor so the filter is byte-identical across
     * tiers — the single-source guarantee (§3.7).
     */
    static filterScenarios(scenarios: EnrichedScenario[], selectedFolder: string, skipViewFolders: string, skipViewAppSites: string): EnrichedScenario[] {
        let filteredScenarios: EnrichedScenario[];
        if (selectedFolder !== '') {
            filteredScenarios = [];
            for (let i = 0; i < scenarios.length; i++) {
                // Filter by exact folder match - case-insensitive like C#
                if (scenarios[i].sourceFolder !== null &&
                    scenarios[i].sourceFolder !== undefined &&
                    scenarios[i].sourceFolder !== '' &&
                    scenarios[i].sourceFolder.toLowerCase() === selectedFolder.toLowerCase()) {
                    filteredScenarios.push(scenarios[i]);
                }
            }
        } else {
            filteredScenarios = scenarios;
        }

        if (skipViewFolders !== '') {
            const skipFolderNames = skipViewFolders.split(',');
            const afterSkip: EnrichedScenario[] = [];
            for (let i = 0; i < filteredScenarios.length; i++) {
                let shouldSkip = false;
                for (let j = 0; j < skipFolderNames.length; j++) {
                    if (filteredScenarios[i].sourceFolder.toLowerCase() === skipFolderNames[j].trim().toLowerCase()) {
                        shouldSkip = true;
                        break;
                    }
                }
                if (!shouldSkip) {
                    afterSkip.push(filteredScenarios[i]);
                }
            }
            filteredScenarios = afterSkip;
        }

        if (skipViewAppSites !== '') {
            const skipAppSiteNames = skipViewAppSites.split(',');
            const afterSkipAppSites: EnrichedScenario[] = [];
            for (let i = 0; i < filteredScenarios.length; i++) {
                let shouldSkip = false;
                for (let j = 0; j < skipAppSiteNames.length; j++) {
                    if (filteredScenarios[i].appSite.toLowerCase() === skipAppSiteNames[j].trim().toLowerCase()) {
                        shouldSkip = true;
                        break;
                    }
                }
                if (!shouldSkip) {
                    afterSkipAppSites.push(filteredScenarios[i]);
                }
            }
            filteredScenarios = afterSkipAppSites;
        }

        return filteredScenarios;
    }

    /**
     * Builds the scenario picker's result JsonObject (Scenarios array + count + the resolved
     * SelectedAppSiteFolder). Shared by the server processData and the client processor.
     */
    static buildScenariosResult(filteredScenarios: EnrichedScenario[], selectedFolder: string): JsonObject {
        const retDataJson = new JsonObject();
        const scenariosArray = new JsonArray();
        for (let i = 0; i < filteredScenarios.length; i++) {
            const scenario = filteredScenarios[i];
            const scenarioObj = new JsonObject();
            scenarioObj.set('appSite', scenario.appSite);
            scenarioObj.set('appView', scenario.appView);
            scenarioObj.set('displayName', scenario.displayName);
            scenarioObj.set('description', scenario.description);
            scenarioObj.set('sourceFolder', scenario.sourceFolder);
            scenariosArray.add(scenarioObj);
        }
        retDataJson.set('Scenarios', scenariosArray);
        retDataJson.set('ScenariosCount', scenariosArray.length);
        if (selectedFolder !== null && selectedFolder !== undefined && selectedFolder !== '') {
            retDataJson.set('SelectedAppSiteFolder', selectedFolder);
        }
        return retDataJson;
    }

    /**
     * Static version of the scenario-field accessor for the shared parse (supports JsonObject/JsonValue).
     */
    private static _getScenarioValueStatic(obj: JsonValue, key: string): string {
        const jsonObj: JsonObject | null = obj instanceof JsonObject ? obj : obj.asObject();
        if (jsonObj !== null) {
            return jsonObj.has(key) ? jsonObj.get(key).toString() : '';
        }
        return '';
    }

    /**
     * Gets enriched scenario for a single scenario. Pure: the readme text is read from the Scenario model
     * (stamped from the index at load), so this performs no file IO.
     */
    private static _getEnrichedScenario(scenario: Scenario, paramJson: JsonObject | null = null): EnrichedScenario {
        const scenarioAppView = scenario.appView || '';
        const scenarioKey = scenarioAppView === ''
            ? `${scenario.appSite}:`
            : `${scenario.appSite}:${scenarioAppView}`;

        // Display text from the readme metadata stamped on the Scenario. An empty readmeTitle means the
        // AppSite had no readme.txt or an empty line 2 — the same condition the previous in-handler reader
        // expressed as "not found", so the fallback order below is unchanged.
        let displayName = '';
        let description = '';
        const readmeTitle = scenario.readmeTitle || '';

        if (readmeTitle !== '') {
            // displayName = "AppSite - Title" or "AppSite - Title -> AppView"
            displayName = scenarioAppView === ''
                ? `${scenario.appSite} - ${readmeTitle}`
                : `${scenario.appSite} - ${readmeTitle} -> ${scenarioAppView}`;
            description = scenario.readmeDescription || '';

            // Append AppView to description if present
            if (scenarioAppView !== '' && description !== '') {
                description = `${description} [${scenarioAppView}]`;
            }
        }

        // If readme metadata not present or empty, try paramJson
        if (displayName === '' && paramJson !== null && paramJson !== undefined) {
            let hasKey = false;
            if (paramJson instanceof Map) {
                hasKey = paramJson.has(scenarioKey);
            } else if (typeof (paramJson as { has?: (k: string) => boolean }).has === 'function') {
                hasKey = (paramJson as { has: (k: string) => boolean }).has(scenarioKey);
            } else if (typeof (paramJson as { containsKey?: (k: string) => boolean }).containsKey === 'function') {
                hasKey = (paramJson as { containsKey: (k: string) => boolean }).containsKey(scenarioKey);
            } else {
                hasKey = scenarioKey in paramJson;
            }

            if (hasKey) {
                let metadataValue: JsonValue | undefined;
                if (paramJson instanceof Map) {
                    metadataValue = paramJson.get(scenarioKey);
                } else if (typeof (paramJson as { get?: (k: string) => JsonValue }).get === 'function') {
                    metadataValue = (paramJson as { get: (k: string) => JsonValue }).get(scenarioKey);
                } else {
                    metadataValue = (paramJson as unknown as Record<string, JsonValue>)[scenarioKey];
                }

                if (metadataValue !== null && metadataValue !== undefined) {
                    // Unwrap JsonValue to JsonObject for field access
                    const metaObj: JsonObject | null = metadataValue instanceof JsonObject
                        ? metadataValue
                        : metadataValue.asObject();

                    if (metaObj !== null) {
                        if (metaObj.has('displayName')) {
                            const dn = metaObj.get('displayName');
                            if (!dn.isNull) {
                                displayName = dn.toString();
                            }
                        }
                        if (metaObj.has('description')) {
                            const desc = metaObj.get('description');
                            if (!desc.isNull) {
                                description = desc.toString();
                            }
                        }
                    }
                }
            }
        }

        // Fall back to defaults if still empty
        if (displayName === '') {
            displayName = scenarioAppView === ''
                ? scenario.appSite
                : `${scenario.appSite} -> ${scenarioAppView}`;
        }
        if (description === '') {
            description = scenarioAppView === ''
                ? `Template of ${scenario.appSite}`
                : `Template of ${scenario.appSite} [${scenarioAppView}]`;
        }

        return new EnrichedScenario(
            scenario.appSite,
            scenarioAppView,
            displayName,
            description,
            scenario.sourceFolder || ''
        );
    }
}
